import { useMemo } from 'react';
import { MoldOrder } from '../../types/mold';
import { FileResponse } from '../../types/moldOrder';
import { getDraftRejectPhotoTag } from '../utils/moldOrder';
import ImagesUploader from '../Questionnaire/ImagesUploader';
import { ArrowDown, X } from 'lucide-react';
import RejectReasonSelector from './RejectReasonSelector';

export type CustomModalType = 'confirmation' | 'success' | 'error' | 'accept' | 'reject' | 'rework';

interface CustomModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  note: string;
  setNote: (note: string) => void;
  modalType: CustomModalType;
  children?: React.ReactNode;
  isAllowSetNextAssignee: boolean;
  rejectReasons: string[] | null;
  rejectPhotos: FileResponse[];
  moldOrder: MoldOrder;
  onFileChange?: () => void;
  files: FileResponse[];
  NextAssigneeComponent: () => JSX.Element;
  UpdateActionComponent: () => JSX.Element | null;
}

const CustomModal: React.FC<CustomModalProps> = ({
  isOpen,
  title,
  message,
  onClose,
  onConfirm,
  isLoading,
  note,
  setNote,
  modalType,
  children,
  isAllowSetNextAssignee,
  rejectReasons,
  rejectPhotos,
  moldOrder,
  files,
  NextAssigneeComponent,
  UpdateActionComponent,
}) => {
  if (!isOpen) return null;

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value);
  };

  const isRejectAction = modalType === 'reject';

  const cssBorderStyle = () => {
    if (modalType === 'reject') {
      return 'border-2 border-red-500';
    } else if (modalType === 'accept') {
      return 'border-2 border-teal-500';
    } else if (modalType === 'rework') {
      return 'border-2 border-yellow-500';
    }
    return 'border-2 border-blue-500';
  }

  const getTextStyle = () => {
    if (modalType === 'reject') {
      return 'text-red-500';
    } else if (modalType === 'accept') {
      return 'text-teal-500';
    } else if (modalType === 'rework') {
      return 'text-yellow-500';
    }
    return 'text-blue-500';
  }
  const textStyle = getTextStyle();
  const getBgButtonStyle = () => {
    if (modalType === 'reject') {
      return 'bg-red-500 hover:bg-red-600';
    } else if (modalType === 'accept') {
      return 'bg-teal-500 hover:bg-teal-600';
    }
    return 'bg-blue-500 hover:bg-blue-600';
  }
  const bgButtonStyle = getBgButtonStyle();

  const enablePhotoAttachment = modalType === 'reject';
  const widthStyle = modalType === 'reject' ? 'max-w-3xl' : 'max-w-md'

  // Memoize the ImagesUploader component to prevent re-renders when note changes
  const memoizedImageUploader = useMemo(() => {
    if (!enablePhotoAttachment) return null;

    // Get the reject photo tag once to avoid recalculating it
    const rejectPhotoTag = getDraftRejectPhotoTag(moldOrder);
    // Pre-filter the photos with the specific tag to avoid filtering on each render
    const filteredRejectPhotos = files.filter(file => file.tag === rejectPhotoTag);

    return (
      <div className="w-full py-2 flex flex-col items-start mb-2">
        <ImagesUploader
          key={`reject-photo-${rejectPhotoTag}-${filteredRejectPhotos.length}`}
          title={rejectPhotoTag}
          displayTitle="รูปภาพประกอบเหตุผลในการปฏิเสธ"
          headerComponent={<div className="text-gray-600 mb-1 text-sm">รูปภาพประกอบเหตุผลในการปฏิเสธ (ถ้ามี)</div>}
          moldOrder={moldOrder}
          entityId={moldOrder.id}
          entityType={'mold_order'}
          files={filteredRejectPhotos}
          tag={rejectPhotoTag}
          hideTag={true}
        />
      </div>
    );
  }, [
    enablePhotoAttachment,
    rejectPhotos.length,
    moldOrder.id,         // Only depend on id instead of the entire object
    moldOrder.status,     // Only depend on status which affects the tag
    moldOrder.orderDetail?.rejectPhotoTag, // Only depend on specific property needed
    files
  ]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className={`relative bg-white rounded-lg w-full ${widthStyle} mx-4 z-50 ${cssBorderStyle()}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${textStyle}`}>{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className={`text-gray-600 mb-4 text-lg ${textStyle}`}>{message}</p>

          {/* Reject reasons component with separated states */}
          {rejectReasons && (
            <RejectReasonSelector
              reasons={rejectReasons}
              setNote={(note) => { handleNoteChange({ target: { value: note } } as React.ChangeEvent<HTMLTextAreaElement>) }}
            />
          )}

          {/* Regular note field for non-reject actions */}
          {!rejectReasons && (
            <>
              <p className="text-gray-600 mb-1 text-sm">
                {isRejectAction ? 'กรุณาแนบเหตุผลในการไม่อนุมัติใบงานนี้' : 'คุณสามารถเพิ่มคำสั่งหรือหมายเหตุเพิ่มเติม:'}
              </p>
              <div className="w-full">
                <textarea
                  className="w-full p-2.5 sm:p-3 rounded-md border border-gray-300"
                  value={note}
                  onChange={handleNoteChange}
                  rows={4}
                  placeholder={isRejectAction ? 'เพิ่มเหตุผลในการไม่อนุมัติใบงาน...' : 'เพิ่มคำสั่งหรือหมายเหตุเพิ่มเติม...'}
                />
              </div>
            </>
          )}

          {(children && isAllowSetNextAssignee) && (
            <div className="w-full py-2 flex flex-col items-center">
              <ArrowDown className="h-10 w-10 text-gray-300" />
              <NextAssigneeComponent />
            </div>
          )}

          <UpdateActionComponent />

          {/* Use the memoized ImagesUploader instead of rendering it directly */}
          {memoizedImageUploader}

          <div className="flex justify-end gap-2 w-full">
            <button
              onClick={onClose}
              data-cy="cancel-button"
              className="grow px-4 py-2 text-lg text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              ยกเลิก
            </button>
            <button
              onClick={onConfirm}
              data-cy="confirm-button"
              disabled={isLoading}
              className={`grow px-4 py-2 text-lg text-white rounded-md ${isLoading
                ? 'bg-blue-400 cursor-not-allowed'
                : bgButtonStyle}`}
            >
              {isLoading ? 'กำลังดำเนินการ...' : 'ยืนยัน'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomModal;